/**
 * Error Message Utility
 * Translates technical error messages into user-friendly language
 */

// Common error patterns and their user-friendly messages
const ERROR_PATTERNS = {
  // Network and connection errors
  'Network Error': 'Unable to connect to the server. Please check your internet connection and try again.',
  'timeout': 'The request took too long. Please try again.',
  'ECONNREFUSED': 'Unable to connect to the server. Please try again later.',
  
  // Authentication errors
  'Invalid credentials': 'The email or password you entered is incorrect. Please try again.',
  'Incorrect email or password': 'The email or password you entered is incorrect. Please try again.',
  'User not found': 'No account found with this email address.',
  'Token expired': 'Your session has expired. Please log in again.',
  'Invalid token': 'Your session is invalid. Please log in again.',
  'Unauthorized': 'You don\'t have permission to perform this action.',
  'Not authenticated': 'Please log in to continue.',
  
  // Validation errors
  'Validation error': 'Please check your input and try again.',
  'Invalid email': 'Please enter a valid email address.',
  'Invalid phone': 'Please enter a valid phone number.',
  'Invalid URL': 'Please enter a valid website address.',
  'Required field': 'This field is required.',
  'Field is required': 'This field is required.',
  'Value too short': 'This value is too short.',
  'Value too long': 'This value is too long.',
  'Invalid format': 'The format of this value is incorrect.',
  'Invalid date': 'Please enter a valid date.',
  
  // Duplicate/unique errors
  'already exists': 'This value is already in use. Please choose a different one.',
  'Duplicate': 'This value is already in use. Please choose a different one.',
  'must be unique': 'This value is already in use. Please choose a different one.',
  'Email already registered': 'An account with this email already exists.',
  
  // Permission errors
  'Permission denied': 'You don\'t have permission to perform this action.',
  'Access denied': 'You don\'t have permission to access this resource.',
  'Forbidden': 'You don\'t have permission to perform this action.',
  
  // Not found errors
  'Not found': 'The requested item could not be found.',
  'Resource not found': 'The requested item could not be found.',
  'User not found': 'The user could not be found.',
  'Brand not found': 'The brand could not be found.',
  'Area not found': 'The area could not be found.',
  'Data input not found': 'The data input could not be found.',
  'Record not found': 'The record could not be found.',
  'Attribute not found': 'The attribute could not be found.',
  
  // File errors
  'File too large': 'The file is too large. Please choose a smaller file.',
  'Invalid file type': 'This file type is not supported. Please choose a different file.',
  'File upload failed': 'Failed to upload the file. Please try again.',
  'File not found': 'The file could not be found.',
  
  // Database errors
  'Database error': 'A system error occurred. Please try again later.',
  'Connection error': 'Unable to connect to the database. Please try again later.',
  
  // Server errors
  'Internal server error': 'A system error occurred. Please try again later.',
  'Server error': 'A system error occurred. Please try again later.',
  '500': 'A system error occurred. Please try again later.',
  '503': 'The service is temporarily unavailable. Please try again later.',
  
  // Rate limiting
  'Too many requests': 'You\'ve made too many requests. Please wait a moment and try again.',
  'Rate limit exceeded': 'You\'ve made too many requests. Please wait a moment and try again.',
}

// Entity-specific error messages
const ENTITY_ERRORS = {
  user: {
    create: 'Unable to create the user. Please check your input and try again.',
    update: 'Unable to update the user. Please check your input and try again.',
    delete: 'Unable to delete the user. Please try again.',
    load: 'Unable to load user information. Please try again.',
    notFound: 'The user could not be found.',
  },
  brand: {
    create: 'Unable to create the brand. Please check your input and try again.',
    update: 'Unable to update the brand. Please check your input and try again.',
    delete: 'Unable to delete the brand. Please try again.',
    load: 'Unable to load brand information. Please try again.',
    notFound: 'The brand could not be found.',
  },
  area: {
    create: 'Unable to create the area. Please check your input and try again.',
    update: 'Unable to update the area. Please check your input and try again.',
    delete: 'Unable to delete the area. Please try again.',
    load: 'Unable to load area information. Please try again.',
    notFound: 'The area could not be found.',
  },
  dataInput: {
    create: 'Unable to create the data input. Please check your input and try again.',
    update: 'Unable to update the data input. Please check your input and try again.',
    delete: 'Unable to delete the data input. Please try again.',
    load: 'Unable to load data input information. Please try again.',
    notFound: 'The data input could not be found.',
  },
  record: {
    create: 'Unable to create the record. Please check your input and try again.',
    update: 'Unable to update the record. Please check your input and try again.',
    delete: 'Unable to delete the record. Please try again.',
    load: 'Unable to load record information. Please try again.',
    notFound: 'The record could not be found.',
    bulkUpload: 'Unable to upload the records. Please check your file and try again.',
  },
  attribute: {
    create: 'Unable to create the attribute. Please check your input and try again.',
    update: 'Unable to update the attribute. Please check your input and try again.',
    delete: 'Unable to delete the attribute. Please try again.',
    load: 'Unable to load attribute information. Please try again.',
    notFound: 'The attribute could not be found.',
  },
  export: {
    create: 'Unable to export the data. Please try again.',
    download: 'Unable to download the export file. Please try again.',
    load: 'Unable to load export information. Please try again.',
  },
}

/**
 * Get a user-friendly error message from an error object
 * @param {Error|Object} error - The error object
 * @param {string} fallbackMessage - Fallback message if no match found
 * @returns {string} User-friendly error message
 */
export function getUserFriendlyErrorMessage(error, fallbackMessage = 'Something went wrong. Please try again.') {
  // Handle null/undefined errors
  if (!error) {
    return fallbackMessage
  }

  // Extract error message from various error formats
  let errorMessage = ''
  
  if (typeof error === 'string') {
    errorMessage = error
  } else if (error.response?.data?.detail) {
    // FastAPI error format
    if (typeof error.response.data.detail === 'string') {
      errorMessage = error.response.data.detail
    } else if (error.response.data.detail.message) {
      errorMessage = error.response.data.detail.message
    }
  } else if (error.response?.data?.message) {
    errorMessage = error.response.data.message
  } else if (error.message) {
    errorMessage = error.message
  }

  // Check if it's already a user-friendly message (doesn't contain technical terms)
  const technicalTerms = ['null', 'undefined', 'NaN', 'TypeError', 'ReferenceError', 'SyntaxError', 'status code', 'HTTP']
  const isTechnical = technicalTerms.some(term => errorMessage.toLowerCase().includes(term.toLowerCase()))
  
  if (!isTechnical && errorMessage.length > 0 && errorMessage.length < 200) {
    // If it's already user-friendly and not too long, return it
    // But still check against our patterns for better messages
    for (const [pattern, friendlyMessage] of Object.entries(ERROR_PATTERNS)) {
      if (errorMessage.toLowerCase().includes(pattern.toLowerCase())) {
        return friendlyMessage
      }
    }
    return errorMessage
  }

  // Try to match against known patterns
  for (const [pattern, friendlyMessage] of Object.entries(ERROR_PATTERNS)) {
    if (errorMessage.toLowerCase().includes(pattern.toLowerCase())) {
      return friendlyMessage
    }
  }

  // Return fallback message
  return fallbackMessage
}

/**
 * Get entity-specific error message
 * @param {string} entity - Entity type (user, brand, area, etc.)
 * @param {string} action - Action type (create, update, delete, load)
 * @param {Error|Object} error - The error object (optional)
 * @returns {string} User-friendly error message
 */
export function getEntityErrorMessage(entity, action, error = null) {
  const entityErrors = ENTITY_ERRORS[entity]
  
  if (entityErrors && entityErrors[action]) {
    // If we have a specific error, try to get a better message
    if (error) {
      const friendlyMessage = getUserFriendlyErrorMessage(error, entityErrors[action])
      // If the friendly message is different from the default, use it
      if (friendlyMessage !== entityErrors[action]) {
        return friendlyMessage
      }
    }
    return entityErrors[action]
  }
  
  // Fallback to generic message
  return getUserFriendlyErrorMessage(error)
}

/**
 * Format validation errors into user-friendly messages
 * @param {Array|Object} validationErrors - Validation errors from backend
 * @returns {string} Formatted error message
 */
export function formatValidationErrors(validationErrors) {
  if (!validationErrors) {
    return 'Please check your input and try again.'
  }

  // Handle array of errors
  if (Array.isArray(validationErrors)) {
    if (validationErrors.length === 0) {
      return 'Please check your input and try again.'
    }
    
    if (validationErrors.length === 1) {
      const error = validationErrors[0]
      return formatSingleValidationError(error)
    }
    
    // Multiple errors - show first one with count
    const firstError = formatSingleValidationError(validationErrors[0])
    return `${firstError} (and ${validationErrors.length - 1} more error${validationErrors.length > 2 ? 's' : ''})`
  }
  
  // Handle single error object
  return formatSingleValidationError(validationErrors)
}

/**
 * Format a single validation error
 * @param {Object} error - Single validation error
 * @returns {string} Formatted error message
 */
function formatSingleValidationError(error) {
  if (typeof error === 'string') {
    return getUserFriendlyErrorMessage(error)
  }
  
  const field = error.attribute_label || error.attribute_name || error.field || 'This field'
  const message = error.error_message || error.message || 'is invalid'
  
  // Combine field and message
  return `${field}: ${message}`
}

/**
 * Get error message for network/connection issues
 * @returns {string} User-friendly network error message
 */
export function getNetworkErrorMessage() {
  return 'Unable to connect to the server. Please check your internet connection and try again.'
}

/**
 * Get error message for authentication issues
 * @returns {string} User-friendly auth error message
 */
export function getAuthErrorMessage() {
  return 'Your session has expired. Please log in again.'
}

export default {
  getUserFriendlyErrorMessage,
  getEntityErrorMessage,
  formatValidationErrors,
  getNetworkErrorMessage,
  getAuthErrorMessage,
}

